import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import AdminShell from "@/components/AdminShell";
import { Eyebrow } from "@/components/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConversationsView() {
  const [conversations, setConversations] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchConversations = useCallback(async (search = "") => {
    setLoading(true);
    try {
      const url = search ? `${API}/admin/conversations?search=${encodeURIComponent(search)}` : `${API}/admin/conversations`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations);
    } catch (error) {
      toast.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchConversations(searchQuery);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Convert to CSV
      const data = response.data.data;
      if (data.length === 0) {
        toast.error("No data to export");
        return;
      }

      const headers = Object.keys(data[0]);
      const csv = [
        headers.join(','),
        ...data.map(row => 
          headers.map(header => {
            const value = row[header]?.toString() || '';
            return `"${value.replace(/"/g, '""')}"`;
          }).join(',')
        )
      ].join('\n');

      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversations_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();

      toast.success("Data exported successfully");
    } catch (error) {
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminShell active="conversations">
      {/* Page header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button">
          ← Back
        </Button>
      </div>
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow className="mb-3">Oversight</Eyebrow>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-ink tracking-tight leading-[1.05]">All Conversations</h1>
        </div>
        <Button
          onClick={handleExport}
          disabled={exporting}
          className="h-10 px-5"
          data-testid="export-button"
        >
          {exporting ? "Exporting..." : "Export to CSV"}
        </Button>
      </div>

      {/* Search */}
      <div className="card-flat p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-3">
          <Input
            placeholder="Search by email or message content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1"
            data-testid="search-input"
          />
          <Button type="submit" data-testid="search-button" className="h-10">
            Search
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-10"
            onClick={() => {
              setSearchQuery("");
              fetchConversations();
            }}
            data-testid="clear-search-button"
          >
            Clear
          </Button>
        </form>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversations List */}
        <section className="card-flat">
          <div className="px-6 py-5 border-b border-line">
            <h2 className="text-base font-bold text-ink tracking-tight">Conversations <span className="text-mute font-mono font-normal text-sm">({conversations.length})</span></h2>
          </div>
          <div className="p-4">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-line border-t-equal mx-auto"></div>
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8 text-mute" data-testid="no-conversations-message">
                No conversations found.
              </div>
            ) : (
              <ScrollArea className="h-[600px]">
                <div className="space-y-2">
                  {conversations.map((conv) => (
                    <div
                      key={conv.id}
                      className={`p-4 border rounded-[10px] cursor-pointer transition-colors ${
                        selectedConversation?.id === conv.id ? 'bg-mint border-equal' : 'border-line hover:bg-page'
                      }`}
                      onClick={() => setSelectedConversation(conv)}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="flex items-start justify-between mb-1.5 gap-2">
                        <h4 className="font-semibold text-ink text-sm truncate">{conv.user_email}</h4>
                        <div className="flex gap-2 flex-shrink-0">
                          {conv.session_ended && (
                            <span className="pill" data-testid={`session-ended-${conv.id}`}>
                              Session Ended
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-mute font-mono">
                        {conv.messages?.length || 0} messages • {new Date(conv.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </section>

        {/* Conversation Detail */}
        <section className="card-flat">
          <div className="px-6 py-5 border-b border-line">
            <h2 className="text-base font-bold text-ink tracking-tight">Conversation Details</h2>
          </div>
          <div className="p-6">
            {!selectedConversation ? (
              <div className="text-center py-12 text-mute text-sm" data-testid="select-conversation-message">
                Select a conversation to view details
              </div>
            ) : (
              <div>
                <div className="mb-4 pb-4 border-b border-line space-y-0.5">
                  <p className="text-sm text-mute">User: <span className="font-semibold text-ink">{selectedConversation.user_email}</span></p>
                  <p className="text-sm text-mute">Started: <span className="font-mono text-xs text-ink-2">{new Date(selectedConversation.created_at).toLocaleString()}</span></p>
                  {selectedConversation.session_ended && (
                    <p className="text-sm text-mute">Ended: <span className="font-mono text-xs text-ink-2">{new Date(selectedConversation.session_ended_at).toLocaleString()}</span></p>
                  )}
                </div>

                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {selectedConversation.messages?.map((msg, idx) => (
                      <div key={idx} className={`p-3.5 rounded-[10px] border ${
                        msg.role === 'user' ? 'bg-mint border-[#cfe9db]' : 'bg-page border-line'
                      }`} data-testid={`message-${idx}`}>
                        <div className="flex items-start justify-between mb-2">
                          <span className="th-mono">{msg.role}</span>
                          {msg.flagged_for_human && (
                            <span className="text-xs px-2 py-0.5 rounded-md bg-warm text-[#c47a12] border border-[#f3d9b0]" data-testid={`flagged-${idx}`}>
                              🚩 Flagged
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-ink whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                        <p className="text-xs text-mute mt-2 font-mono">
                          {new Date(msg.timestamp).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>
        </section>
      </div>
    </AdminShell>
  );
}
